import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneTilt, Envelope, X, Check, Spinner, UserCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Peer-to-peer flashcard sharing UI on the Vokabular page.
// Two pieces:
//   1. <VocabShareInbox /> — collapsible bell showing pending shares from
//      buddies. Each card has a checkbox so the receiver picks which ones
//      to keep. On accept, the sender gets +1 point per accepted card.
//   2. <SendCardsButton /> — opens a dialog where the owner picks N buddies
//      to send the chosen card to. Sends one share per buddy.

export const VocabShareInbox = ({ onAccepted }) => {
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({}); // share_id -> Set<card_id>

  const load = async () => {
    try {
      const res = await axios.get(`${API}/flashcards/received`, { withCredentials: true });
      setShares(res.data || []);
      // default: all cards selected
      const next = {};
      (res.data || []).forEach(s => { next[s.share_id] = new Set(s.cards.map(c => c.card_id)); });
      setSelected(next);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const toggle = (shareId, cardId) => {
    setSelected(prev => {
      const cur = new Set(prev[shareId] || []);
      if (cur.has(cardId)) cur.delete(cardId); else cur.add(cardId);
      return { ...prev, [shareId]: cur };
    });
  };

  const accept = async (share) => {
    const keep = Array.from(selected[share.share_id] || []);
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/flashcards/received/${share.share_id}/accept`,
        { accepted_card_ids: keep },
        { withCredentials: true },
      );
      toast.success(`${res.data.count} Karten in dein Deck übernommen`);
      onAccepted?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const reject = async (share) => {
    if (!window.confirm('Diesen Versand verwerfen?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/flashcards/received/${share.share_id}/reject`, {}, { withCredentials: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  if (shares.length === 0) return null;
  return (
    <div className="bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-2xl p-4" data-testid="vocab-inbox">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2"
        data-testid="vocab-inbox-toggle"
      >
        <span className="inline-flex items-center gap-2 text-[#292524] font-heading font-semibold">
          <Envelope size={18} weight="fill" className="text-violet-600" />
          {shares.length} {shares.length === 1 ? 'neuer Versand' : 'neue Versendungen'} von Freunden
        </span>
        <span className="text-xs text-violet-700 font-semibold">{open ? 'Einklappen' : 'Öffnen'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {shares.map(s => (
            <div key={s.share_id} className="bg-white border border-violet-200 rounded-xl p-3" data-testid={`vocab-share-${s.share_id}`}>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <p className="text-sm font-semibold text-[#292524] flex items-center gap-1.5">
                  <UserCircle size={14} className="text-violet-600" weight="fill" />
                  {s.sender_name}
                  <span className="text-xs text-[#78716C] font-normal">· {s.folder ? `Ordner „${s.folder}“ · ` : ''}{s.cards.length} {s.cards.length === 1 ? 'Karte' : 'Karten'}</span>
                </p>
                <span className="text-[10px] text-[#A8A29E]">{new Date(s.created_at).toLocaleDateString('de-DE')}</span>
              </div>
              <ul className="space-y-1">
                {s.cards.map(c => {
                  const isSel = (selected[s.share_id] || new Set()).has(c.card_id);
                  return (
                    <li key={c.card_id}
                        className={`flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer ${isSel ? 'bg-violet-50' : 'bg-stone-50 opacity-60'}`}
                        onClick={() => toggle(s.share_id, c.card_id)}
                        data-testid={`vocab-share-card-${c.card_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        readOnly
                        className="w-4 h-4 accent-violet-600 cursor-pointer"
                      />
                      <span className="font-semibold text-[#292524]">{c.word}</span>
                      <span className="text-[#78716C]">→ {c.translation}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => accept(s)}
                  disabled={busy || (selected[s.share_id] || new Set()).size === 0}
                  className="inline-flex items-center gap-1 bg-violet-600 hover:bg-violet-700 disabled:bg-stone-300 text-white text-xs font-semibold px-3 py-1.5 rounded"
                  data-testid={`vocab-share-accept-${s.share_id}`}
                >
                  {busy ? <Spinner size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
                  Auswahl übernehmen ({(selected[s.share_id] || new Set()).size})
                </button>
                <button
                  onClick={() => reject(s)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 text-xs font-semibold px-3 py-1.5 rounded"
                  data-testid={`vocab-share-reject-${s.share_id}`}
                >
                  <X size={12} /> Verwerfen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SendCardsButton = ({ card, folder }) => {
  const [open, setOpen] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const loadBuddies = async () => {
    try {
      const res = await axios.get(`${API}/buddies/my-buddies`, { withCredentials: true });
      setBuddies(res.data || []);
    } catch { setBuddies([]); }
  };

  const toggle = (uid) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const send = async () => {
    if (picked.size === 0) return toast.error('Wähle mindestens einen Freund');
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/flashcards/send`,
        { ...(folder ? { folder } : { card_ids: [card.card_id] }), recipient_ids: Array.from(picked) },
        { withCredentials: true },
      );
      toast.success(`An ${res.data.created.length} Freund(e) gesendet`);
      setOpen(false);
      setPicked(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); loadBuddies(); }}
        className={folder
          ? 'inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 text-sm font-semibold'
          : 'text-xs px-2 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded hover:bg-violet-100'}
        title="An Freunde schicken"
        data-testid={folder ? 'folder-send-btn' : `card-send-${card.card_id}`}
      >
        <PaperPlaneTilt size={folder ? 16 : 12} className="inline" weight="fill" /> {folder ? `Ordner „${folder}“ senden` : 'An Freund senden'}
      </button>
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="vocab-send-dialog"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-heading font-bold text-[#292524]">{folder ? `Ordner „${folder}“ an Freunde schicken` : `Karte „${card.word}" an Freunde schicken`}</h3>
              <button onClick={() => !busy && setOpen(false)} className="p-1 hover:bg-stone-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-2 max-h-[300px] overflow-y-auto">
              {buddies.length === 0 ? (
                <p className="text-sm text-[#78716C] italic">Noch keine Freunde verbunden. Geh in „Buddy" und schick eine Anfrage.</p>
              ) : buddies.map(b => {
                const buddy = b.buddy || b;  // depending on endpoint shape
                const uid = buddy.user_id || b.user_id;
                const name = buddy.name || buddy.email || 'Buddy';
                return (
                  <button
                    key={uid}
                    onClick={() => toggle(uid)}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded ${picked.has(uid) ? 'bg-violet-100' : 'hover:bg-stone-50'}`}
                    data-testid={`vocab-send-target-${uid}`}
                  >
                    <input type="checkbox" checked={picked.has(uid)} readOnly className="accent-violet-600" />
                    <span className="text-sm">{name}</span>
                  </button>
                );
              })}
            </div>
            <div className="p-3 border-t border-stone-200 flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} disabled={busy} className="text-sm px-3 py-1.5 rounded border border-stone-300 hover:bg-stone-50">Abbrechen</button>
              <button
                onClick={send}
                disabled={busy || picked.size === 0}
                className="text-sm px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-700 disabled:bg-stone-300 text-white font-semibold inline-flex items-center gap-1"
                data-testid="vocab-send-submit"
              >
                {busy ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />}
                Senden ({picked.size})
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
